/**
 * Resolve the canonical catalog row for a medication name.
 *
 * A clinic can accumulate several non-archived medications that share a name —
 * most often one per unit (e.g. "Protonix 40 mg" in TABLETS, BOTTLES, and MG).
 * The medication name dropdown collapses these to a single entry, and the dose
 * form looks the medication back up by name to prefill from its template. When
 * the lookups disagree about which row wins, the row carrying the dosing template
 * can lose, so the template (amount, unit, frequency, administration location,
 * lot) never prefills.
 *
 * This picks one row per name deterministically: prefer the row that has a dosing
 * template, falling back to the first match when none does. Both the dropdown and
 * the dose form use this so they always resolve to the same row.
 */
export function pickMedicationByName<
  T extends { name: string; template?: unknown },
>(medications: T[], name: string): T | undefined {
  const matches = medications.filter((med) => med.name === name);
  return matches.find((med) => med.template) ?? matches[0];
}

/**
 * Build a name → canonical-row lookup in a single pass.
 *
 * Equivalent to calling {@link pickMedicationByName} for every unique name, but
 * linear in the number of medications instead of quadratic — preferred when a
 * component needs the canonical row for many names at once (e.g. the medication
 * dropdown). Applies the same tie-break: the first row wins unless a later row
 * with the same name carries a template and the current pick does not.
 */
export function canonicalMedicationsByName<
  T extends { name: string; template?: unknown },
>(medications: T[]): Map<string, T> {
  const byName = new Map<string, T>();
  for (const med of medications) {
    const current = byName.get(med.name);
    if (!current || (med.template && !current.template)) {
      byName.set(med.name, med);
    }
  }
  return byName;
}
